import { useMemo, type ReactNode } from "react";

export function MarkdownMessage({
  text,
  collapseEvidence = false,
  evidence,
  evidenceTestId = "assistant-answer-evidence"
}: {
  text: string;
  collapseEvidence?: boolean;
  evidence?: ReactNode;
  evidenceTestId?: string;
}) {
  const sections = useMemo(() => markdownSections(text), [text]);
  const blocks = collapseEvidence ? sections.main : sections.all;
  const hasEvidence = collapseEvidence && (sections.evidence.length > 0 || evidence);

  return (
    <div className="tcsAssistantMarkdown grid gap-1.5">
      {renderMarkdownBlocks(blocks, "main")}
      {hasEvidence ? (
        <details
          data-testid={evidenceTestId}
          className="rounded-[10px] border border-[var(--line2)] bg-[var(--raised2)] px-2.5"
        >
          <summary className="flex min-h-[40px] cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-extrabold text-[var(--ink2)] marker:hidden">
            <span>Why &amp; sources</span>
            <span
              aria-hidden="true"
              className="grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border border-[var(--line3)] text-[11px] leading-none text-[var(--muted)]"
            >
              +
            </span>
          </summary>
          {sections.evidence.length > 0 ? (
            <div className="grid gap-1.5 border-t border-[var(--line2)] py-2.5">
              {renderMarkdownBlocks(sections.evidence, "evidence")}
            </div>
          ) : null}
          {evidence ? (
            <div className="grid gap-2 border-t border-[var(--line2)] py-2.5">
              {evidence}
            </div>
          ) : null}
        </details>
      ) : evidence ? (
        evidence
      ) : null}
    </div>
  );
}

type MarkdownBlock =
  | { kind: "heading"; level: 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "divider" };

interface MarkdownSections {
  all: MarkdownBlock[];
  main: MarkdownBlock[];
  evidence: MarkdownBlock[];
}

const EVIDENCE_HEADINGS = new Set([
  "why",
  "why & sources",
  "why and sources",
  "why this answer",
  "why it matters",
  "source",
  "sources",
  "fact",
  "facts",
  "project facts",
  "evidence",
  "technical details"
]);

function renderMarkdownBlocks(
  blocks: readonly MarkdownBlock[],
  keyPrefix: string
): ReactNode[] {
  return blocks.map((block, index) => {
    if (block.kind === "heading") {
      const HeadingTag = block.level === 2 ? "h2" : "h3";

      return (
        <HeadingTag
          key={`${keyPrefix}-${block.kind}-${index}`}
          className="m-0 text-[13px] font-extrabold leading-[1.25] text-[var(--ink)]"
        >
          <InlineMarkdown text={block.text} />
        </HeadingTag>
      );
    }

    if (block.kind === "list") {
      return (
        <ul
          key={`${keyPrefix}-${block.kind}-${index}`}
          className="m-0 grid list-disc gap-1 pl-4"
        >
          {block.items.map((item, itemIndex) => (
            <li key={`${item}-${itemIndex}`}>
              <InlineMarkdown text={item} />
            </li>
          ))}
        </ul>
      );
    }

    if (block.kind === "divider") {
      return (
        <hr
          key={`${keyPrefix}-${block.kind}-${index}`}
          className="my-1.5 border-t border-[var(--line2)]"
        />
      );
    }

    return (
      <p
        key={`${keyPrefix}-${block.kind}-${index}`}
        className="m-0 whitespace-pre-wrap"
      >
        <InlineMarkdown text={block.text} />
      </p>
    );
  });
}

function markdownSections(text: string): MarkdownSections {
  const all = markdownBlocks(text);
  const evidenceIndex = all.findIndex(
    (block) => block.kind === "heading" && isEvidenceHeading(block.text)
  );

  if (evidenceIndex < 0) {
    return { all, main: all, evidence: [] };
  }

  return {
    all,
    main: all.slice(0, evidenceIndex),
    evidence: all.slice(evidenceIndex)
  };
}

function isEvidenceHeading(text: string): boolean {
  const normalised = text
    .replace(/[\*_`]/g, "")
    .replace(/[：:]+$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

  return EVIDENCE_HEADINGS.has(normalised);
}

function markdownBlocks(text: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  let paragraph: string[] = [];
  let list: string[] = [];
  const flushParagraph = () => {
    if (paragraph.length === 0) {
      return;
    }

    blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
    paragraph = [];
  };
  const flushList = () => {
    if (list.length === 0) {
      return;
    }

    blocks.push({ kind: "list", items: list });
    list = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    if (trimmed.match(/^---+$/)) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "divider" });
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);

    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({
        kind: "heading",
        level: heading[1]?.length === 2 ? 2 : 3,
        text: heading[2] ?? ""
      });
      continue;
    }

    const evidenceLine = trimmed.match(
      /^(?:\*\*)?\s*(Why(?:\s+(?:&|and)\s+sources|\s+this\s+answer|\s+it\s+matters)?|Sources?|Facts?|Project facts|Evidence|Technical details)(?=\s*(?::|\*\*|$))\s*:?(?:\*\*)?\s*(.*)$/i
    );

    if (evidenceLine) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "heading", level: 3, text: evidenceLine[1] ?? "" });

      if (evidenceLine[2]?.trim()) {
        blocks.push({ kind: "paragraph", text: evidenceLine[2].trim() });
      }

      continue;
    }

    const listItem = trimmed.match(/^[-*]\s+(.+)$/);

    if (listItem) {
      flushParagraph();
      list.push(listItem[1] ?? "");
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();

  return blocks.length > 0 ? blocks : [{ kind: "paragraph", text }];
}

function InlineMarkdown({ text }: { text: string }) {
  const parts = useMemo(() => {
    const result: Array<{ strong: boolean; text: string }> = [];
    const pattern = /\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        result.push({ strong: false, text: text.slice(lastIndex, match.index) });
      }

      result.push({ strong: true, text: match[1] ?? "" });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      result.push({ strong: false, text: text.slice(lastIndex) });
    }

    return result.length > 0 ? result : [{ strong: false, text }];
  }, [text]);

  return (
    <>
      {parts.map((part, index) =>
        part.strong ? (
          <strong key={`${part.text}-${index}`} className="font-extrabold">
            {part.text}
          </strong>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        )
      )}
    </>
  );
}
